// Stage 4's split replay: the common operating picture as it was on the left, ground truth on the
// right, on one shared scrubber (spec §2 Stage 4).
//
// Both panes are plain 2-D canvases over the AO's static north-up `basemap.png` rather than two more
// MapLibre instances: the debrief never pans or zooms, so a second WebGL map per pane would cost two
// tile pipelines for a picture that never moves.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { AoMeta, Event, Graphic, Unit } from '../../sim/types';
import { TICKS_PER_MIN } from '../../sim/types';
import type { TruthFrame } from '../../sim/sim';
import { REPLAY_FPS, REPLAY_STEP_TICKS, useDebrief } from '../../state/debrief';
import { clockHM } from '../reports';
import { contactReportMeta, contactSpans, frameAt } from './format';
import { contactRingAlpha, contactSpreadM } from '../../cop/spread';
import { useBranch } from './branchState';

const CANVAS_PX = 520;

/** The replay's palette, exported for the Decision Support Template, which draws the same AO on the
 * same basemap with the same control graphics (src/ui/debrief/DecisionSupport.tsx). */
export const COLOR = {
  friendly: '#58c4ff',
  hostile: '#ff5a5a',
  isr: '#7ee081',
  warn: '#ffb24d',
  paper: '#e6ebf0',
};

/** One reported contact, resolved to what a canvas needs to draw its ring: spec §7's ring-size-is-
 * uncertainty rule, computed the same way `LiveStage` computes it for the live overlay. */
interface ReplayContact {
  cell: number;
  /** Ring radius in meters (src/cop/spread.ts), grown by the contact's age at the scrubbed tick. */
  errorM: number;
  ringAlpha: number;
}

// ---- dark basemap -----------------------------------------------------------------------------------
//
// tools/ao/build_ao.py bakes `basemap.png` for a flat daylight paper map: grey and desaturated. Retoning
// it toward the ops-center dark map the player actually fought on (spec §8) is a canvas-level transform,
// not a re-render of the terrain, so it is done once per image and cached rather than redone on every
// scrub tick or every re-render of both panes.

const darkBasemapCache = new Map<string, HTMLCanvasElement>();

/** `--color-ink`, resolved from the page rather than hard-coded, so the pane always matches the token. */
function inkColor(): string {
  if (typeof document === 'undefined') return '#0b0f14';
  const v = getComputedStyle(document.documentElement).getPropertyValue('--color-ink').trim();
  return v || '#0b0f14';
}

/**
 * Multiplies the basemap toward `--color-ink` at partial strength: Porter-Duff "multiply" composited at
 * alpha `DARK_ALPHA` over an opaque backdrop works out to `result = base * ((1 - alpha) + alpha * ink)`,
 * i.e. a single draw call that both pulls the hue toward the ops-center slate and knocks brightness down
 * by roughly `alpha` (≈45% at 0.45, since ink is close to black) - and because multiply is monotonic per
 * channel, relief shading and roads stay in the same relative order, just quieter: hillshade is still
 * visible, roads are still the faintly lighter thing running across the terrain.
 */
function darkenBasemap(img: HTMLImageElement): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(img, 0, 0);
  const DARK_ALPHA = 0.45;
  ctx.globalCompositeOperation = 'multiply';
  ctx.globalAlpha = DARK_ALPHA;
  ctx.fillStyle = inkColor();
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.globalCompositeOperation = 'source-over';
  ctx.globalAlpha = 1;
  return canvas;
}

/** The AO's darkened basemap, loaded and retoned once per AO name and reused by both panes — and by
 * the Decision Support Template, which is the same plate with decision-point markers on it. */
export function useDarkBasemap(name: string): HTMLCanvasElement | null {
  const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(() => darkBasemapCache.get(name) ?? null);
  useEffect(() => {
    const cached = darkBasemapCache.get(name);
    if (cached) { setCanvas(cached); return; }
    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const dark = darkenBasemap(img);
      darkBasemapCache.set(name, dark);
      setCanvas(dark);
    };
    img.src = `/ao/${name}/basemap.png`;
    return () => { cancelled = true; };
  }, [name]);
  return canvas;
}

export function Replay({
  meta, truth, events, units, graphics,
}: {
  meta: AoMeta;
  truth: TruthFrame[];
  events: Event[];
  units: Unit[];
  graphics: Graphic[];
}) {
  // When a branch has been flown (pitch 6), the right pane stops showing what happened and starts
  // showing what would have happened instead — on the same scrubber, minute for minute, because the
  // two runs share every tick before the decision point.
  const branch = useBranch((s) => s.outcome);
  const clearBranch = useBranch((s) => s.clearOutcome);
  const scrubTick = useDebrief((s) => s.scrubTick);
  const playing = useDebrief((s) => s.playing);
  const setScrubTick = useDebrief((s) => s.setScrubTick);
  const setPlaying = useDebrief((s) => s.setPlaying);

  const altTruth = branch?.result.truth ?? null;
  const realEnd = truth.length > 0 ? truth[truth.length - 1].tick : 0;
  const altEnd = altTruth && altTruth.length > 0 ? altTruth[altTruth.length - 1].tick : 0;
  // The alternate run can outlast the real one (or end sooner); the scrubber has to reach the end of
  // whichever is longer or half of one pane is unreachable.
  const endTick = Math.max(realEnd, altEnd);
  const spans = useMemo(() => contactSpans(events), [events]);
  const contactMeta = useMemo(() => contactReportMeta(events), [events]);
  const unitById = useMemo(() => new Map(units.map((u) => [u.id, u])), [units]);
  // The AO's basemap is a single 1200x1200 north-up PNG covering the grid exactly (tools/ao/build_ao.py),
  // retoned dark once per AO name (see `useDarkBasemap` above) to match the live map's ops-center tone.
  const basemap = useDarkBasemap(meta.name);

  // Playback: one ground-truth frame (one sim minute) every 100 ms, stopping at the end of the run.
  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => {
      const s = useDebrief.getState();
      const next = s.scrubTick + REPLAY_STEP_TICKS;
      if (next >= endTick) { s.setScrubTick(endTick); s.setPlaying(false); return; }
      s.setScrubTick(next);
    }, 1000 / REPLAY_FPS);
    return () => clearInterval(timer);
  }, [playing, endTick]);

  const frame = useMemo(() => frameAt(truth, scrubTick), [truth, scrubTick]);
  const altFrame = useMemo(() => (altTruth ? frameAt(altTruth, scrubTick) : null), [altTruth, scrubTick]);
  // The contacts blue actually held at the scrubbed tick, each ring sized by the age of *that* report at
  // *that* tick (src/cop/spread.ts) - not the report's original error bar - so the left pane replays the
  // uncertainty the player actually saw, growing and fading exactly as the live overlay did.
  const visibleContacts: ReplayContact[] = useMemo(
    () => spans
      .filter((s) => s.from <= scrubTick && s.until > scrubTick)
      .map((s) => {
        const m = contactMeta.get(s.eventId);
        const ageMin = Math.max(0, (scrubTick - s.from) / TICKS_PER_MIN);
        return {
          cell: s.cell,
          errorM: contactSpreadM(m?.typeConf ?? 'unknown', ageMin, m?.errorM ?? 0),
          ringAlpha: contactRingAlpha(ageMin),
        };
      }),
    [spans, contactMeta, scrubTick],
  );

  return (
    <section data-testid="replay" className="wt-panel p-3">
      <div className="grid grid-cols-2 gap-3">
        <Pane
          title="WHAT YOU SAW"
          subtitle="Your common operating picture: your own units, and every enemy contact your observers had reported and not yet lost."
          tone={COLOR.friendly}
          meta={meta} basemap={basemap} graphics={graphics}
          frame={frame} unitById={unitById}
          side="cop" contacts={visibleContacts}
        />
        {branch ? (
          <Pane
            title="WHAT WOULD HAVE HAPPENED"
            subtitle={`Branched at ${clockHM(branch.branchTick)} — ${branch.dpTitle}, "${branch.optionLabel}" instead. Every tick before that minute is the fight you actually fought; the enemy was not re-rolled.`}
            tone={COLOR.warn}
            testId="replay-pane-branch"
            meta={meta} basemap={basemap} graphics={graphics}
            frame={altFrame} unitById={unitById}
            side="truth" contacts={[]}
          />
        ) : (
          <Pane
            title="WHAT HAPPENED"
            subtitle="Ground truth: every unit alive at this minute, on both sides, wherever it really was."
            tone={COLOR.hostile}
            meta={meta} basemap={basemap} graphics={graphics}
            frame={frame} unitById={unitById}
            side="truth" contacts={[]}
          />
        )}
      </div>

      {branch && (
        <div className="mt-2 flex items-center gap-2">
          <span
            data-testid="replay-branch-note"
            className="mono text-[10px] uppercase tracking-[0.14em]"
            style={{ color: 'var(--color-warn)' }}
          >
            right pane: branch of {branch.dpTitle}
          </span>
          <button
            type="button"
            data-testid="replay-branch-clear"
            onClick={clearBranch}
            className="mono rounded-sm border border-slate-700 px-1.5 py-0.5 text-[10px] uppercase tracking-[0.12em] text-fog hover:text-[color:var(--color-friendly)]"
          >
            show what happened
          </button>
        </div>
      )}

      <div className="mt-3 flex items-center gap-3">
        <button
          type="button"
          data-testid="replay-play"
          onClick={() => {
            if (!playing && scrubTick >= endTick) setScrubTick(0);
            setPlaying(!playing);
          }}
          className="mono rounded-sm border px-2 py-1 text-[11px] uppercase tracking-[0.18em]"
          style={{
            borderColor: 'var(--color-friendly)',
            color: 'var(--color-friendly)',
            background: 'color-mix(in srgb, var(--color-friendly) 10%, transparent)',
          }}
        >
          {playing ? 'pause' : 'play'}
        </button>
        {/* A slider, not a dropdown: the one range input in the app (spec §8 bans select elements only). */}
        <input
          type="range"
          data-testid="replay-scrub"
          min={0}
          max={endTick}
          step={REPLAY_STEP_TICKS}
          value={Math.min(scrubTick, endTick)}
          onChange={(e) => { setPlaying(false); setScrubTick(Number(e.target.value)); }}
          className="h-1 flex-1 accent-[color:var(--color-friendly)]"
          aria-label="Replay time"
        />
        <span className="mono w-32 text-right text-[12px] text-paper" data-testid="replay-clock">
          {clockHM(scrubTick)} / {clockHM(endTick)}
        </span>
      </div>
    </section>
  );
}

function Pane({
  title, subtitle, tone, meta, basemap, graphics, frame, unitById, side, contacts, testId,
}: {
  title: string;
  subtitle: string;
  tone: string;
  /** Overrides the pane's own `replay-pane-<side>` handle when two panes draw the same side. */
  testId?: string;
  meta: AoMeta;
  basemap: HTMLCanvasElement | null;
  graphics: Graphic[];
  frame: TruthFrame | null;
  unitById: Map<number, Unit>;
  side: 'cop' | 'truth';
  contacts: ReplayContact[];
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    drawPane(ctx, {
      size: CANVAS_PX, meta, basemap, graphics, frame, unitById, side, contacts,
    });
  }, [meta, basemap, graphics, frame, unitById, side, contacts]);

  return (
    <div>
      <div className="mono flex items-baseline gap-2 text-[11px] tracking-[0.18em]" style={{ color: tone }}>
        {title}
      </div>
      {/* The frame is tinted by the pane's own tone (friendly cyan for the COP, hostile red for truth)
          at low alpha, so the two plates read as distinct pictures at a glance, ops-center style. */}
      <canvas
        ref={canvasRef}
        width={CANVAS_PX}
        height={CANVAS_PX}
        data-testid={testId ?? `replay-pane-${side}`}
        className="mt-1 w-full rounded-sm border"
        style={{
          background: 'var(--color-ink)',
          aspectRatio: '1 / 1',
          borderColor: `color-mix(in srgb, ${tone} 35%, var(--color-slate-700))`,
        }}
      />
      <p className="mt-1 text-[11px] leading-snug text-fog">{subtitle}</p>
    </div>
  );
}

// ---- drawing ---------------------------------------------------------------------------------------

interface PaneDraw {
  size: number;
  meta: AoMeta;
  basemap: HTMLCanvasElement | null;
  graphics: Graphic[];
  frame: TruthFrame | null;
  unitById: Map<number, Unit>;
  side: 'cop' | 'truth';
  contacts: ReplayContact[];
}

function drawPane(ctx: CanvasRenderingContext2D, d: PaneDraw): void {
  const { size, meta } = d;
  const spanCm = meta.w * meta.cellM * 100;
  // The AO frame has y running north; the basemap and the canvas both run south, hence the flip.
  const px = (x: number): number => (x / spanCm) * size;
  const py = (y: number): number => size - (y / spanCm) * size;
  const mToPx = (m: number): number => (m * 100 / spanCm) * size;

  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = '#0b0f14';
  ctx.fillRect(0, 0, size, size);
  if (d.basemap) {
    // Already darkened toward `--color-ink` once per AO (see `darkenBasemap`); drawn at full opacity
    // here so the retone is what the player sees, not a screenshot faded a second time.
    ctx.drawImage(d.basemap, 0, 0, size, size);
  }

  drawGraphics(ctx, d.graphics, px, py, mToPx);

  if (!d.frame) return;

  if (d.side === 'truth') {
    for (const u of d.frame.units) {
      if (!u.alive) continue;
      const real = d.unitById.get(u.id);
      if (!real) continue;
      drawMarker(ctx, px(u.x), py(u.y), real.side === 0 ? 'blue' : 'red', real.callsign, u.strength);
    }
    return;
  }

  // The picture as blue held it: own units where they were, contacts at the cell the report named.
  for (const u of d.frame.units) {
    if (!u.alive) continue;
    const real = d.unitById.get(u.id);
    if (!real || real.side !== 0) continue;
    drawMarker(ctx, px(u.x), py(u.y), 'blue', real.callsign, u.strength);
  }
  const cellCm = meta.cellM * 100;
  for (const c of d.contacts) {
    const cx = (c.cell % meta.w) * cellCm + cellCm / 2;
    const cy = Math.floor(c.cell / meta.w) * cellCm + cellCm / 2;
    drawContactRing(ctx, px(cx), py(cy), mToPx(c.errorM), c.ringAlpha);
  }
}

export function drawGraphics(
  ctx: CanvasRenderingContext2D,
  graphics: Graphic[],
  px: (x: number) => number,
  py: (y: number) => number,
  mToPx: (m: number) => number,
): void {
  ctx.lineWidth = 1.25;
  ctx.font = '9px "IBM Plex Mono", ui-monospace, monospace';
  ctx.textAlign = 'center';

  for (const g of graphics) {
    const pts = g.pts.map((p) => ({ x: px(p.x), y: py(p.y) }));
    if (pts.length === 0) continue;
    switch (g.kind) {
      case 'axis':
      case 'pl': {
        ctx.setLineDash([]);
        ctx.strokeStyle = COLOR.friendly;
        ctx.globalAlpha = g.kind === 'pl' ? 0.9 : 0.7;
        ctx.beginPath();
        pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
        ctx.stroke();
        ctx.fillStyle = COLOR.friendly;
        ctx.fillText(g.name, pts[0].x, pts[0].y - 4);
        break;
      }
      case 'obj': {
        ctx.setLineDash([]);
        ctx.strokeStyle = COLOR.friendly;
        ctx.globalAlpha = 0.95;
        const r = mToPx(g.radiusM ?? 800);
        ctx.beginPath();
        if (pts.length === 1) ctx.arc(pts[0].x, pts[0].y, r, 0, Math.PI * 2);
        else { pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y))); ctx.closePath(); }
        ctx.stroke();
        ctx.fillStyle = COLOR.friendly;
        ctx.fillText(g.name, pts[0].x, pts[0].y - r - 4);
        break;
      }
      case 'nai': {
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = COLOR.isr;
        ctx.globalAlpha = 0.9;
        const r = mToPx(g.radiusM ?? 400);
        ctx.beginPath();
        ctx.arc(pts[0].x, pts[0].y, r, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = COLOR.isr;
        ctx.fillText(g.name, pts[0].x, pts[0].y - r - 4);
        break;
      }
      case 'aa': {
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = COLOR.friendly;
        ctx.globalAlpha = 0.6;
        ctx.beginPath();
        pts.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
        ctx.closePath();
        ctx.stroke();
        ctx.setLineDash([]);
        break;
      }
    }
  }
  ctx.globalAlpha = 1;
}

/**
 * A reported contact: a translucent disc sized by `errorM` (position uncertainty, grown by age - src/
 * cop/spread.ts) plus a hollow diamond at its centre, in the same proportions the live PixiJS overlay
 * uses (`Overlay.layoutContacts`) so the replay is honestly the picture the player had, not a redrawn
 * approximation of it.
 */
function drawContactRing(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  rPx: number,
  ringAlpha: number,
): void {
  const r = Math.max(3, rPx);
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = COLOR.hostile;
  ctx.globalAlpha = 0.1 * ringAlpha * Math.min(1, 70 / r);
  ctx.fill();
  ctx.lineWidth = 1;
  ctx.strokeStyle = COLOR.hostile;
  ctx.globalAlpha = 0.45 * ringAlpha;
  ctx.stroke();

  const d = 4;
  ctx.beginPath();
  ctx.moveTo(x, y - d);
  ctx.lineTo(x + d, y);
  ctx.lineTo(x, y + d);
  ctx.lineTo(x - d, y);
  ctx.closePath();
  ctx.lineWidth = 1.5;
  ctx.globalAlpha = 0.95 * ringAlpha;
  ctx.stroke();
  ctx.globalAlpha = 1;
}

/**
 * MIL-STD-2525 affiliation shapes in miniature: a friendly cyan square, a hostile red diamond. The full
 * milsymbol sprites the live overlay uses are far more than a 520 px pane can resolve.
 */
function drawMarker(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  affiliation: 'blue' | 'red',
  label: string | null,
  strength: number,
): void {
  const r = 4.5;
  const color = affiliation === 'blue' ? COLOR.friendly : COLOR.hostile;
  ctx.globalAlpha = strength < 40 ? 0.55 : 1;
  ctx.fillStyle = color;
  ctx.strokeStyle = '#0b0f14';
  ctx.lineWidth = 1;

  ctx.beginPath();
  if (affiliation === 'blue') {
    ctx.rect(x - r, y - r, r * 2, r * 2);
  } else {
    ctx.moveTo(x, y - r * 1.3);
    ctx.lineTo(x + r * 1.3, y);
    ctx.lineTo(x, y + r * 1.3);
    ctx.lineTo(x - r * 1.3, y);
    ctx.closePath();
  }
  ctx.fill();
  ctx.stroke();

  if (label) {
    ctx.globalAlpha = 0.9;
    ctx.fillStyle = COLOR.paper;
    ctx.font = '8px "IBM Plex Mono", ui-monospace, monospace';
    ctx.textAlign = 'center';
    ctx.fillText(label, x, y + r + 9);
  }
  ctx.globalAlpha = 1;
}
